import React, { useState, useEffect } from 'react'; 
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import QuestionIcon from '@mui/icons-material/HelpOutline';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpChangelogIcon from '@mui/icons-material/ArrowUpward'; 
import ArrowDownChangelogIcon from '@mui/icons-material/ArrowDownward';
import InputAdornment from '@mui/material/InputAdornment';
import Search from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import BellIcon from '@mui/icons-material/NotificationsActive';

import { useAppDispatch, useAppSelector } from '../../core/hooks';
import { 
    updateSymbol, updateView, selectView, 
    updateTraderType, selectFilters, selectFiltersLastUpdated,
    selectSortCriteria, selectSortDirections, selectSortLastUpdated, updateSortCriteria, updateSortDirections, 
    selectTraderType,
    savePrevValues,
    selectSymbol,
    selectSecurityView
} from '../../core/reducers/reducer-main';
import { timeframeOrdering, isOfTypeTimeframe, conditionsOrdering, traderToTimeframes, traderToTimeframeAbbreviations, ViewTypes } from '../../core/datatypes/main';
import BiasSquare from '../BiasSquare';
import { viewTypeIsMultiView } from '../../core/datatypes/main';
import useWindowDimensions from '../../core/customHooks/windowDimensions';
import styles from './datarows.module.css';
import { slimGreen, slimGrey, slimRed } from '../../assets/colors/palette';
import SearchBar from '../SearchBar';
import {selectDiscordUserName, selectUID} from '../../core/reducers/reducer-user';
import {selectSubscribedUserData} from '../../core/reducers/reducer-subscribed-bias';
import SubscribedBiasUserCheckBoxDropdown from "./SubscribedBiasUserCheckBoxDropdown";

const tradeValueRankings = [
    "Very Bearish", "Bearish", "Slightly Bearish", "Slightly Bearish to Neutral",
    "Neutral", "Neutral to Slightly Bullish", "Slightly Bullish to Neutral", "Slightly Bullish", 
    "Bullish", "Very Bullish"
];

const squareThreshold1 = 1296;

interface DatarowProps {
    columns: Array<any>,
    data: Array<any>, 
    view: ViewTypes,
    lastUpdate: number, 
    validTradeData: any
}

const Datarows = (props: DatarowProps) => {
    const [lastEffect, setLastEffect] = useState((new Date()).getTime());
    const [datarows, setDatarows] = useState(props.data);
    const dispatch = useAppDispatch(); 
    const view = useAppSelector(selectView); 
    const securityView = useAppSelector(selectSecurityView)
    const traderType = useAppSelector(selectTraderType);
    const filters = useAppSelector(selectFilters);
    const filtersLastUpdated = useAppSelector(selectFiltersLastUpdated);
    const sortCriteria = useAppSelector(selectSortCriteria); 
    const sortDirections = useAppSelector(selectSortDirections);
    const sortLastUpdated = useAppSelector(selectSortLastUpdated);
    const [datarowsSearchQuery, setDatarowsSearchQuery] = useState('');
    const userID = useAppSelector(selectUID);
    const discordUserName = useAppSelector(selectDiscordUserName);
    const symbol = useAppSelector(selectSymbol);
    const biasSubs = useAppSelector(selectSubscribedUserData);
    const dimensions = useWindowDimensions();
    const [noMatchingRowsFound, setNoMatchingRowsFound] = useState(false);
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    useEffect(() => {
        function handleResize() {
        setWindowDimensions({
            width: window.innerWidth,
            height: window.innerHeight,
        });
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDataRowsSearchQueryChange = (value: string) => {
        setDatarowsSearchQuery(value);
    };

    useEffect(() => {        
        let rowsOrdered = props.data;
        if (filters.length > 0) {
            const filtersApplied = rowsOrdered.filter((el) => {
                let isElPassing = true; 
                filters.forEach(filter => {
                    if (filter.includes('scan-')) {
                        const filterLessPrefix = filter.replace("scan-", ""); 
                        if (el.trade_setup_condition !== undefined) {
                            if (el.trade_setup_condition.condition === null) {
                                isElPassing = false;
                            } else {
                                const elConditionLowered = el.trade_setup_condition.condition.toLowerCase();
                                isElPassing = isElPassing && elConditionLowered.includes(filterLessPrefix); 
                            }
                        }
                    } else if (filter.includes('security-type:')) {
                        const filterLessPrefix = filter.replace("security-type:", "");
                        if (el.security_type !== undefined) {
                            if (el.security_type === null) {
                                isElPassing = false;
                            } else {
                                const currSecurityTypeCategory = filterLessPrefix === "equity" ? 1 : filterLessPrefix === "etfs" ? 2 : 0;
                                isElPassing = isElPassing && el.security_type === currSecurityTypeCategory;
                            }
                        }
                    }
                })
                return isElPassing;
            })
            rowsOrdered = filtersApplied;
        } 

        let sortDone = false;
        if (sortCriteria.length > 0) {
            // For now fix to length 1 
            const sorted = rowsOrdered.sort((a,b) => {
                if (sortCriteria[0] === "trade_setup_condition") {
                    if (("trade_setup_condition" in a) && ("trade_setup_condition" in b)) {
                        sortDone = sortDone || true;
                        const aIdx = conditionsOrdering.indexOf(a.trade_setup_condition.condition);
                        const bIdx = conditionsOrdering.indexOf(b.trade_setup_condition.condition);
                        if (aIdx === bIdx) {
                            const aNewest = Math.max(...Object.keys(a.trade_setup_condition.historical_json).map(el=>parseFloat(el)));
                            const bNewest = Math.max(...Object.keys(b.trade_setup_condition.historical_json).map(el=>parseFloat(el))); 
                            return bNewest - aNewest;
                        } else {
                            if (sortDirections[0] === 'ascending') {
                                return aIdx - bIdx;
                            } else {
                                return bIdx - aIdx;
                            }
                        }
                    }
                } else if (sortCriteria[0] == "notify_setup_condition") {
                    sortDone = sortDone || true;
                    const isATracked = (biasSubs.filter(el=>(el.symbol===a.symbol) && (el.trader_type===traderType)).length > 0) ? 1 : 0; 
                    const isBTracked = (biasSubs.filter(el=>(el.symbol===b.symbol) && (el.trader_type===traderType)).length > 0) ? 1 : 0; 
                    if (sortDirections[0] === 'ascending') {
                        return isATracked - isBTracked;
                    } else {
                        return isBTracked - isATracked;
                    }
                } else if (sortCriteria[0] === "symbol" || sortCriteria[0] === "name" || sortCriteria[0] === "industry_group") {
                    sortDone = sortDone || true;
                    const aField = a[sortCriteria[0]] ? a[sortCriteria[0]].toLowerCase() : '';
                    const bField = b[sortCriteria[0]] ? b[sortCriteria[0]].toLowerCase() : '';
                    if (sortDirections[0] === 'ascending') {
                        return bField.localeCompare(aField);
                    } else {
                        return aField.localeCompare(bField);
                    }
                }
                return 1;
            });            
            rowsOrdered = sorted;
        } 

        if (!sortDone) {
            const initialOrderingRows = rowsOrdered.sort((a,b) => {
                if ("symbol" in a && securityView === "all" || securityView === "etfs") {
                    const anchors = ["SPY", "QQQ", "IWM", "DIA"];
                    if ((anchors.indexOf(a.symbol) !== -1) && anchors.indexOf(b.symbol) === -1) {
                        return -1
                    } else if ((anchors.indexOf(a.symbol) === -1) && anchors.indexOf(b.symbol) !== -1) {
                        return 1
                    } else if ((anchors.indexOf(a.symbol) !== -1) && anchors.indexOf(b.symbol) !== -1) {
                        return anchors.indexOf(a.symbol) - anchors.indexOf(b.symbol)
                    }
                    return a.symbol.localeCompare(b.symbol);
                } else if ("symbol" in a) { 
                    if ('industry_group' in a && 'industry_group' in b) {
                        if (a.industry_group !== null && b.industry_group !== null) {
                            return a.industry_group.localeCompare(b.industry_group);
                        }
                    }
                }
                else if ("timeframe" in a) {
                    const aIdx = timeframeOrdering.indexOf(a.timeframe);
                    const bIdx = timeframeOrdering.indexOf(b.timeframe);
                    return aIdx - bIdx;
                }
                return 1;
            })
            rowsOrdered = initialOrderingRows;
        }        

        // Finally, handle search queries. Remove any rows that don't match the search query
        // and bump rows with an exact match to the top
        if (datarowsSearchQuery && rowsOrdered[0] && rowsOrdered[0].symbol) {
            rowsOrdered = rowsOrdered
                .filter((el) => el.symbol.includes(datarowsSearchQuery))
                .sort((a, b) => {
                    const aSymbol : string = a.symbol.trim();
                    const bSymbol : string = b.symbol.trim();
                    const isGoodMatchA = aSymbol.startsWith(datarowsSearchQuery);
                    const isGoodMatchB = bSymbol.startsWith(datarowsSearchQuery);
                
                    if (isGoodMatchA && !isGoodMatchB) {
                        return -1;
                    }

                    if (isGoodMatchB && !isGoodMatchA) {
                        return 1;
                    }
                
                    return aSymbol.localeCompare(bSymbol);
                });
        }

        if (rowsOrdered.length > 0) {
            setNoMatchingRowsFound(false);
        } else {
            setNoMatchingRowsFound(true);
        }

        const nextDatarows = rowsOrdered; 
        setDatarows(nextDatarows);
        setLastEffect((new Date()).getTime());
    }, [props.data, props.lastUpdate, props.view, securityView, filtersLastUpdated, sortLastUpdated, datarowsSearchQuery])

    const handleHeaderClick = (column: any) => {
        if (
            (column.field === "trade_setup_condition") || (column.field === "notify_setup_condition" || 
                (column.field === "symbol") || (column.field === "name") || (column.field === "industry_group")
            )) { 
            // temporarily set criteria to max 1 item, so if another col is clicked, just cancels out current one
            const columnSortCriteriaIndex = sortCriteria.indexOf(column.field);
            if (columnSortCriteriaIndex === -1 && sortCriteria.length === 0) {
                const nextSortCriteria = [column.field, ...sortCriteria]; 
                const nextSortDirections = new Array<'ascending' | 'descending'>('descending', ...sortDirections);
                dispatch(updateSortCriteria(nextSortCriteria)); 
                dispatch(updateSortDirections(nextSortDirections));
            } else {
                const currColumnSortDirection = sortDirections[columnSortCriteriaIndex]; 
                if (currColumnSortDirection === "descending") {
                    const nextSortDirections = [...sortDirections];
                    nextSortDirections[columnSortCriteriaIndex] = 'ascending'; 
                    dispatch(updateSortDirections(nextSortDirections));
                } else {
                    const nextSortCriteria = [...sortCriteria]; 
                    const nextSortDirections = [...sortDirections]; 
                    nextSortCriteria.splice(columnSortCriteriaIndex, 1);
                    nextSortDirections.splice(columnSortCriteriaIndex, 1);
                    dispatch(updateSortCriteria(nextSortCriteria));
                    dispatch(updateSortDirections(nextSortDirections));
                }
            }
        }
    }      

    const handleSymbolClick = (symbol: string) => {
        if (view === "traders-list") {
            // dispatch(updateTraderType('all'));
            dispatch(savePrevValues());
            dispatch(updateView(traderType)); 
            dispatch(updateSymbol(symbol))
        } else {
            dispatch(savePrevValues());
            dispatch(updateTraderType('all'));
            dispatch(updateView('all')); 
            dispatch(updateSymbol(symbol))
        }
    }

    let validTradeConditionComp : any = undefined;
    if (Object.keys(props.validTradeData).length > 0) {         
        if (Object.keys(props.validTradeData).length > 0) {
            const validTradeCondition = props.validTradeData.condition;
            let lastUpdateComp = <div></div>;
            if (Object.keys(props.validTradeData.historical_json).length > 0) {
                const newest = Math.max(...Object.keys(props.validTradeData.historical_json).map(el=>parseFloat(el)));
                const lastUpdateDate = new Date(newest*1000); 
                const displayDate = [lastUpdateDate.getMonth() + 1, lastUpdateDate.getDate(), lastUpdateDate.getFullYear()].join('/');

                const hourFormatted = (lastUpdateDate.getHours() === 0 || lastUpdateDate.getHours() === 12 ) ? 12 : lastUpdateDate.getHours() % 12; 
                const minFormatted = lastUpdateDate.getMinutes() < 10 ? '0' + lastUpdateDate.getMinutes().toString() : lastUpdateDate.getMinutes()
                const timeTag = lastUpdateDate.getHours() < 12 ? 'am' : 'pm';
                const displayTime = [hourFormatted, minFormatted].join(':') + timeTag;
                
                const newestHistoricalItem = props.validTradeData.historical_json[newest.toString()];
                const arrowIcon = (tradeValueRankings.indexOf(newestHistoricalItem.prev_text) < tradeValueRankings.indexOf(newestHistoricalItem.next_text) ? <ArrowUpChangelogIcon style={{color: slimGreen}}/> : <ArrowDownChangelogIcon style={{color: slimRed}}/>);             
                
                lastUpdateComp = (
                    <div className={styles.tradeSetupCellLastUpdate}>
                        <div className={styles.tradeSetupCellLastUpdateTextStackCont}>
                            <div>
                                {displayDate}
                            </div>
                            <div>
                                {displayTime}
                            </div>
                        </div>
                        <div className={styles.tradeSetupCellLastUpdateArrowCont}>{arrowIcon}</div>
                    </div>
                )
                
            }
            const coloringStyle = (validTradeCondition.toLowerCase().indexOf("bearish") !== -1) ? slimRed : 
                (validTradeCondition.toLowerCase().indexOf("bullish") !== -1) ? slimGreen : 
                "#848484";
            validTradeConditionComp = (
                <div className={styles.validTradeCont}>
                    <div className={[styles.cell, styles.cellHeader].join(' ')}>
                        Trade Set-Up Condition
                    </div>
                    <div className={styles.validTradeConditionCell}>
                        <div className={styles.validTradeConditionText} style={{color: coloringStyle}}>
                            {validTradeCondition}
                        </div>
                        {lastUpdateComp}
                    </div> 
                </div>
            )
        }
        else {
            validTradeConditionComp = (
                <div className={styles.validTradeCont}>
                    <div className={[styles.cell, styles.cellHeader].join(' ')}>
                        Trade Set-Up Condition
                    </div>
                    <div className={styles.validTradeConditionCell}>
                        <div className={styles.validTradeConditionText} style={{color: "#848484"}}>
                            Pending
                        </div>
                    </div> 
                </div>
            )
        }
    }

    const dbTooltipText = "The momentum condition in the middle period indicates the primary directional bias. When further alignment in the other time frames appear the “trade set-up condition” will suggest an overall position bias.";

    const markup = () => {
        const columns = securityView !== "equity" ? props.columns.filter(c => c.field !== "industry_group") : props.columns;
        return (
            columns.map(c => {
                const isDBColumn = ((c.field === "fifteen_minute") && (view === "intraday")) ||
                ((c.field === "two_hour") && (view === "near_term")) ||
                ((c.field === "daily") && (view === "short_term")) ||
                ((c.field === "weekly") && (view === "intermediate"));

                const rows = datarows.map(r => {    
                    
                    if ((c.field === "reversal_scout") || (c.field === "slim_ribbon")) {                
                        if (r[c.field] !== undefined) {
                            const keyPt2 = ('symbol' in r) ? r.symbol : ('timeframe' in r) ? r.timeframe : '';
                            const size = (windowDimensions.width > squareThreshold1) ? 28 : 16;   
                            
                            return (
                                <div className={`${styles.cell}`} key={[c.field, keyPt2].join('-')}>
                                    <BiasSquare size={size} bias={r[c.field].bias} lastUpdateTS={r[c.field].bias_last_update} />
                                </div>
                            )
                        }
                    }

                    if (isOfTypeTimeframe(c.field)) {
                        if (r[c.field] !== undefined) {
                            const cellClass = isDBColumn ? [styles.cell, styles.highlightCell].join(' ') : styles.cell;
                            const thisKey = [c.field, r.indicator].join('-'); 
                            const size = (windowDimensions.width > squareThreshold1) ? 28 : 16;   

                            return (
                                <div className={cellClass} key={thisKey}>
                                    <BiasSquare size={size} bias={r[c.field].bias} lastUpdateTS={r[c.field].bias_last_update} />
                                </div>
                            )
                        }
                    }

                    if ((c.field === "symbol")) {
                        if (r[c.field] !== undefined) {          
                            const symbolIcon = biasSubs.filter(el => el.symbol === r[c.field] && el.trader_type === traderType).length > 0 ? (<BellIcon/>) : (undefined);        
                            return (
                                <div className={[styles.cell, styles.actionable].join(' ')} key={[c.field, r.symbol].join('-')} 
                                    onClick={() => handleSymbolClick(r[c.field])}
                                >
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        {r[c.field]}
                                        {symbolIcon}
                                    </div>
                                </div>
                            )
                        }
                    }

                    if ((c.field === "name") || (c.field === "industry_group")) {
                        
                        if (r[c.field] !== undefined) {          
                            return (
                                <div className={[styles.cell, styles.longText].join(' ')} key={[c.field, r.symbol].join('-')} 
                                    onClick={() => console.log(r[c.field])}
                                >
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        {r[c.field]}
                                    </div>
                                </div>
                            )
                        }
                    }

                    if ((c.field === "timeframe")) {            
                        if (r[c.field] !== undefined) {
                            return (
                                <div className={[styles.cell].join(' ')} key={[c.field, r.timeframe].join('-')} >
                                    {r[c.field].split('_').map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')}
                                </div>
                            )
                        }
                    }

                    if ((c.field === "indicator")) {            
                        if (r[c.field] !== undefined) {
                            return (
                                <div className={[styles.cell].join(' ')} key={[c.field, r.indicator].join('-')} >
                                    {r[c.field].split('_').map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')}
                                </div>
                            )
                        }
                    }

                    if (c.field === "trade_setup_condition") {                        
                        if (r[c.field] !== undefined) {            
                            
                            if (r[c.field].condition !== null) {
                                const coloringStyle = (r[c.field].condition.toLowerCase().indexOf("bearish") !== -1) ? slimRed :
                                (r[c.field].condition.toLowerCase().indexOf("bullish") !== -1) ? slimGreen : 
                                "#848484";
                                let lastUpdateComp = <div></div>;
                                if (Object.keys(r[c.field].historical_json).length > 0) {
                                    const newest = Math.max(...Object.keys(r[c.field].historical_json).map(el=>parseFloat(el)));
                                    const lastUpdateDate = new Date(newest*1000); 
                                    const displayDate = [lastUpdateDate.getMonth() + 1, lastUpdateDate.getDate(), lastUpdateDate.getFullYear()].join('/');
        
                                    const hourFormatted = (lastUpdateDate.getHours() === 0 || lastUpdateDate.getHours() === 12 ) ? 12 : lastUpdateDate.getHours() % 12; 
                                    const minFormatted = lastUpdateDate.getMinutes() < 10 ? '0' + lastUpdateDate.getMinutes().toString() : lastUpdateDate.getMinutes()
                                    const timeTag = lastUpdateDate.getHours() < 12 ? 'am' : 'pm';
                                    const displayTime = [hourFormatted, minFormatted].join(':') + timeTag;
                                    
                                    const newestHistoricalItem = r[c.field].historical_json[newest.toString()];
                                    
                                    if (newestHistoricalItem !== undefined) {
                                        const arrowIcon = (tradeValueRankings.indexOf(newestHistoricalItem.prev_text) < tradeValueRankings.indexOf(newestHistoricalItem.next_text) ? <ArrowUpChangelogIcon style={{color: slimGreen}}/> : <ArrowDownChangelogIcon style={{color: slimRed}}/>);             
                                        
                                        lastUpdateComp = (
                                            <div className={styles.tradeSetupCellLastUpdate}>
                                                <div className={styles.tradeSetupCellLastUpdateTextStackCont}>
                                                    <div>
                                                        {displayDate}
                                                    </div>
                                                    <div>
                                                        {displayTime}
                                                    </div>
                                                </div>
                                                <div className={styles.tradeSetupCellLastUpdateArrowCont}>{arrowIcon}</div>
                                            </div>
                                        )
                                    }
                                    
                                }
                                
                                return (
                                    <div className={[styles.cell, styles.tradeSetupCell].join(' ')} key={[c.field, r.symbol].join('-')} >
                                        <div className={styles.validTradeConditionText} style={{color: coloringStyle}}>
                                            {r[c.field].condition}
                                        </div>
                                        {lastUpdateComp}
                                    </div>
                                )
                            }
                            else {
                                return (
                                    <div className={[styles.cell].join(' ')} key={[c.field, r.symbol].join('-')} >
                                        <div className={styles.validTradeConditionText} style={{color: "#848484"}}>
                                            {"Pending"}
                                        </div>
                                    </div>
                                )
                            }
                            
                        }
                        
                    }

                    if ((c.field === "notify_setup_condition") && r.symbol != undefined) {
                        const symbol = r.symbol;
                        const biasList = r.bias_list;

                            return (
                                <div className={[styles.cell, styles.dropdownCell].join(' ')} key={[c.field, r.symbol].join('-')}  >
                                    <SubscribedBiasUserCheckBoxDropdown uidProp={userID}
                                                                        symbolProp={symbol}
                                                                        traderTypeProp={traderType}
                                                                        discordUserNameProp={discordUserName}
                                                                        biasListProp={biasList}/>
                                </div>
                            )

                    }

                })

                
                const cellClass = isDBColumn ? [styles.cell, styles.highlightCell].join(' ') : styles.cell;
                const columnSortCriteriaIdx = sortCriteria.indexOf(c.field); 
                const headerIcon = (columnSortCriteriaIdx === -1) ? (<div style={{height: 24}} />) :
                    (sortDirections[columnSortCriteriaIdx] === 'ascending') ? (<ArrowUpIcon />) :
                    (sortDirections[columnSortCriteriaIdx] === 'descending') ? (<ArrowDownIcon />) :
                    (undefined);

                const columnClass = (c.field === "reversal_scout") || (c.field === "slim_ribbon")  ? (
                    `${styles.column} ${styles.columnWithBiasSquare}`
                ) : (styles.column); 
                let subHeaderText = ""; 
                if (view === "traders-list") {
                    subHeaderText = traderToTimeframeAbbreviations[traderType].join(' | ');
                }
                const subHeader = (view === "traders-list" && ((c.field === "reversal_scout") || (c.field === "slim_ribbon"))) ? (
                    <div className={styles.cellSubHeader}>{subHeaderText}</div>
                ) : (view === "traders-list") ? (
                    <div className={styles.cellSubHeader}></div>
                ) : (undefined);
                const cellHeader = isDBColumn ? (
                    <div className={[cellClass, styles.cellHeader].join(' ')} onClick={() => handleHeaderClick(c)}>
                        <div className={styles.dbHeader}>
                            Directional Bias
                            <Tooltip title={dbTooltipText} placement="top">
                                <IconButton aria-label="explanation" size="small">
                                    <QuestionIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                    </div>
                        <div>{c.name}</div>
                    </div>
                ) : (
                    <div className={[cellClass, styles.cellHeader].join(' ')} onClick={() => handleHeaderClick(c)}>
                        <div className={styles.headerSideBySide}>
                            <div>{c.name}</div>
                            {headerIcon}
                        </div>
                        {subHeader}
                    </div>
                )
                return (
                    <div className={columnClass} key={c.field}>
                        {cellHeader}
                        {rows}
                    </div>
                )
            })
        )
    };

    const shouldLeftAlignRows = ((view == traderType) && (view !== 'all'));

    let dataRowsClassName = styles.datarows;
    if (shouldLeftAlignRows) {
        dataRowsClassName += ` ${styles.datarowsLeft}`
    }

    

    if (view != 'all' && symbol && dimensions.width <= 600) {
        // Need a special view for this case
        let coloringStyle = '#848484'; // Grey by default
        let tradeCondition = 'Pending';
        if  (props.validTradeData.condition) {
            tradeCondition = props.validTradeData.condition;
            let formattedVTC = props.validTradeData.condition.toLowerCase();
            if (formattedVTC.indexOf("bearish") !== -1) {
                coloringStyle = slimRed;
            } else if (formattedVTC.indexOf("bullish") !== -1) {
                coloringStyle = slimGreen;
            }
        }    
        let lastUpdateComp = <div></div>;
        if (Object.keys(props.validTradeData.historical_json).length > 0) {
            const newest = Math.max(...Object.keys(props.validTradeData.historical_json).map(el=>parseFloat(el)));
            const lastUpdateDate = new Date(newest*1000); 
            const displayDate = [lastUpdateDate.getMonth() + 1, lastUpdateDate.getDate(), lastUpdateDate.getFullYear()].join('/');

            const hourFormatted = (lastUpdateDate.getHours() === 0 || lastUpdateDate.getHours() === 12 ) ? 12 : lastUpdateDate.getHours() % 12; 
            const minFormatted = lastUpdateDate.getMinutes() < 10 ? '0' + lastUpdateDate.getMinutes().toString() : lastUpdateDate.getMinutes()
            const timeTag = lastUpdateDate.getHours() < 12 ? 'am' : 'pm';
            const displayTime = [hourFormatted, minFormatted].join(':') + timeTag;
            
            const newestHistoricalItem = props.validTradeData.historical_json[newest.toString()];
            const arrowIcon = (tradeValueRankings.indexOf(newestHistoricalItem.prev_text) < tradeValueRankings.indexOf(newestHistoricalItem.next_text) ? <ArrowUpChangelogIcon style={{color: slimGreen}}/> : <ArrowDownChangelogIcon style={{color: slimRed}}/>);             
            
            lastUpdateComp = (
                <div className={styles.tradeSetupCellLastUpdate}>
                    <div className={styles.tradeSetupCellLastUpdateTextStackCont}>
                        <div>
                            {displayDate}
                        </div>
                        <div>
                            {displayTime}
                        </div>
                    </div>
                    <div className={styles.tradeSetupCellLastUpdateArrowCont}>{arrowIcon}</div>
                </div>
            )
            
        }    

        const dataPropsSorted = props.data.sort((a,b) => a.indicator.localeCompare(b.indicator));

        return (
            <div className={styles.specialMobileSingleSymbolView}>
                <h4>
                    Trade Setup Condition
                </h4>
                <strong style={{color: coloringStyle}} className={styles.mobileTSCText}>
                    {tradeCondition}
                </strong>
                {lastUpdateComp}
                <h4>
                    Reversal Scout
                </h4>
                <table className={styles.specialMobileSingleSymbolViewDataTable}>
                    <thead>
                        <tr>
                            <td></td>
                            <td className={`${styles.specialMobileSingleSymbolViewGreyBackground} ${styles.specialMobileSingleSymbolViewGreyBackgroundHelperText}`}>
                                Directional Bias
                                <Tooltip title={dbTooltipText} placement="top" enterTouchDelay={0} leaveTouchDelay={10000}>
                                <IconButton aria-label="explanation" size="small">
                                    <QuestionIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>
                                {traderToTimeframeAbbreviations[view][0] }
                            </td>
                            <td className={styles.specialMobileSingleSymbolViewGreyBackground}>
                                {traderToTimeframeAbbreviations[view][1] }
                            </td>
                            <td>
                                {traderToTimeframeAbbreviations[view][2] }
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {props.columns.map((column, index) => {
                                if (column.field == 'indicator') {
                                    return null;
                                } else {
                                    const size = (windowDimensions.width > squareThreshold1) ? 28 : 16;   

                                    return  <td className={index == 2 ? styles.specialMobileSingleSymbolViewGreyBackground : ''}>
                                        <BiasSquare size={size} bias={dataPropsSorted[0][column.field].bias} lastUpdateTS={0}/>
                                    </td>
                                    
                                } 
                            })}
                        </tr>
                    </tbody>
                </table>
                <h4>
                    Slim Ribbon
                </h4>
                <table className={styles.specialMobileSingleSymbolViewDataTable}>
                    <thead>
                        <tr>
                            <td></td>
                            <td className={`${styles.specialMobileSingleSymbolViewGreyBackground} ${styles.specialMobileSingleSymbolViewGreyBackgroundHelperText}`}>
                                Directional Bias
                                <Tooltip title={dbTooltipText} placement="top" enterTouchDelay={0} leaveTouchDelay={10000}>
                                <IconButton aria-label="explanation" size="small">
                                    <QuestionIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>
                                {traderToTimeframeAbbreviations[view][0] }
                            </td>
                            <td className={styles.specialMobileSingleSymbolViewGreyBackground}>
                                {traderToTimeframeAbbreviations[view][1] }
                            </td>
                            <td>
                                {traderToTimeframeAbbreviations[view][2] }
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {props.columns.map((column, index) => {
                                if (column.field == 'indicator') {
                                    return null;
                                } else {
                                    const size = (windowDimensions.width > squareThreshold1) ? 28 : 16;                                    
                                    return  <td className={index == 2 ? styles.specialMobileSingleSymbolViewGreyBackground : ''}>
                                        <BiasSquare size={size} bias={dataPropsSorted[1][column.field].bias} lastUpdateTS={0}/>
                                    </td>
                                    
                                } 
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    } else {
        return (
            <>
                {viewTypeIsMultiView(props.view) &&
                    <SearchBar defaultValue={datarowsSearchQuery} onChange={handleDataRowsSearchQueryChange} transformUppercase/>
                }
                <div className={dataRowsClassName}>
                    {noMatchingRowsFound 
                        ? <p> Symbol not found </p> 
                        : <>
                        {markup()}
                        {validTradeConditionComp}
                        </>
                    }
                </div>
    
            </>
        )
    }
}

export default Datarows;